import { useEffect, useState } from "react";
import { GameData, TeamTurns } from "../../../../domain/model/Game";
import {
  Answer,
  difficultyStringToDifficulty,
  Question,
  QuestionGiveRatingRequestModel,
} from "../../../../domain/model/Question";
import { Lifeline, LifelineType } from "../../../../domain/model/Lifeline";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { State } from "../../../../domain/model/ResponseState";
import { QuestionGiveRatingUseCase } from "../../../../domain/usecase/question/giveRating/QuestionGiveRatingUseCase";
import { shouldPlaySounds } from "../../../../core/utils/Utils";
import {
  getSoundCompletePath,
  SoundRepository,
  Sounds,
} from "../../../../data/repository/sound/SoundRepository";
import {
  CHARADES_CATEGORY_ID,
  CHARADES_QRCODE_LINK,
} from "../../../../data/constant/Constants";
import {
  GenerateQRCodeUseCase,
  GenerateQRCodeUseCaseImpl,
} from "../../../../domain/usecase/qrcode/generate/UseCase";

export function QuestionViewModel(
  questionGiveRatingUseCase: QuestionGiveRatingUseCase,
  generateQRCodeUseCase: GenerateQRCodeUseCase,
  hasGivenRate: boolean
) {
  const [gameData, setGameData] = useState<GameData | undefined>();
  const [question, setQuestion] = useState<Question | undefined>();
  const [answer, setAnswer] = useState<Answer | undefined>();

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [countdownTimerSeconds, setCountdownTimerSeconds] =
    useState<number>(-1);

  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [hasFirstTeamAnswered, setHasFirstTeamAnswered] =
    useState<boolean>(false);
  const [bothTeamAnswered, setBothTeamAnswered] = useState<boolean>(false);
  const [showWhoAnswered, setShowWhoAnswered] = useState<boolean>(false);
  const [videoFinished, setVideoFinished] = useState<boolean>(false);
  const [isRatingGiven, setIsRatingGiven] = useState<boolean>(hasGivenRate);
  const [giveFeedbackDialogOpen, setGiveFeedbackDialogOpen] = useState(false);

  const [isCharadesCategory, setIsCharadesCategory] = useState<boolean>(false);
  const [isCharadesReadyToPlay, setIsCharadesReadyToPlay] =
    useState<boolean>(false);
  const [charadesQRCodeImage, setCharadesQRCodeImage] = useState<
    string | undefined
  >();

  var timerTicksSoundAudio: HTMLAudioElement;
  var timerFastTicksSoundAudio: HTMLAudioElement;
  const soundRepo = new SoundRepository();

  useEffect(() => {
    if (shouldPlaySounds()) {
      if (countdownTimerSeconds == 0) {
        soundRepo.play(getSoundCompletePath(Sounds.timerTimesUp));
      }
      if (countdownTimerSeconds == 7) {
        if (!hasFirstTeamAnswered) {
          timerTicksSoundAudio = soundRepo.play(
            getSoundCompletePath(Sounds.timerTimerTicks)
          );
        }
      } else if (countdownTimerSeconds == 2) {
        if (timerTicksSoundAudio) {
          timerTicksSoundAudio.pause();
        }
        timerFastTicksSoundAudio = soundRepo.play(
          getSoundCompletePath(Sounds.timerFastTick)
        );
      }
    }
  }, [countdownTimerSeconds]);

  function init(gameData: GameData, question: Question, answer: Answer) {
    setGameData(gameData);
    setQuestion(question);
    setAnswer(answer);

    setIsCharadesCategory(
      detectIfCategoryIsCharades(question.category?._id ?? "")
    );

    if (!detectIfCategoryIsCharades(question.category?._id ?? ""))
      setCountdownTimerSeconds(
        gameData.difficultiesMap.get(
          difficultyStringToDifficulty(question.difficulty!)
        )!
      );
  }

  useEffect(() => {
    if (answer && isCharadesCategory) {
      generateCharadesQRCode();
    }
  }, [answer, isCharadesCategory]);

  function detectIfCategoryIsCharades(categoryId: string): boolean {
    return categoryId == CHARADES_CATEGORY_ID;
  }

  async function generateCharadesQRCode() {
    if (!answer?._id) return;
    const response = await generateQRCodeUseCase.invoke(
      CHARADES_QRCODE_LINK.replaceAll("{:id}", answer?._id)
    );
    switch (response.responseState) {
      case State.Success:
        setCharadesQRCodeImage(response.data?.qrcode);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function findIfAnyLifelinesIsSelectedBeforeTheQuestion(
    lifelines?: Array<Lifeline>
  ): Boolean {
    if (!lifelines) return false;
    return (
      lifelines.filter((lifeline) => !lifeline.disabled && lifeline.selected)
        .length > 0
    );
  }

  function onLifelineClicked(lifelineId: string) {
    if (!gameData) return;

    const isFirstTeam = gameData.currentTeamTurn == TeamTurns.teamA;

    const lifelinesList = isFirstTeam
      ? [...gameData.firstTeamLifelines]
      : [...gameData.secondTeamLifelines];
    const index = lifelinesList.findIndex((l) => l.id == lifelineId);
    const currentLifeline = lifelinesList[index];

    if (bothTeamAnswered) {
      return;
    }
    if (currentLifeline.shouldChooseBeforeQuestion) {
      showSnackbar(t("can_only_choose_this_lifeline_before_question"));
      return;
    }

    lifelinesList.map((l) => {
      l.selected = l.id == lifelineId;
    });

    if (currentLifeline.type == LifelineType.CALL_FRIEND) {
      setCountdownTimerSeconds(countdownTimerSeconds + 30);
    } else if (currentLifeline.type == LifelineType.EXTRA_TIME) {
      setCountdownTimerSeconds(countdownTimerSeconds + 60);
    }
  }

  async function giveRating(
    questionId: string,
    model: QuestionGiveRatingRequestModel
  ) {
    const response = await questionGiveRatingUseCase.invoke(questionId, model);
    switch (response.responseState) {
      case State.Success:
        setIsRatingGiven(true);
        showSnackbar(response.data?.message, "success");
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function stopTimerSounds() {
    try {
      if (timerTicksSoundAudio) {
        timerTicksSoundAudio.pause();
      }

      if (timerFastTicksSoundAudio) {
        timerFastTicksSoundAudio.pause();
      }
    } catch (ignored) {}
  }

  function onAnswerButtonClicked(insuranceEnabled: boolean) {
    if (shouldPlaySounds()) {
      soundRepo.play(getSoundCompletePath(Sounds.xAnsweredPop));
    }
    stopTimerSounds();
    if (hasFirstTeamAnswered) {
      setBothTeamAnswered(true);
      setShowAnswer(true);
    } else {
      setHasFirstTeamAnswered(true);
      setCountdownTimerSeconds(10);

      if (insuranceEnabled) {
        setBothTeamAnswered(true);
        setShowAnswer(true);
      }
    }
  }

  function setTimerPausedOrResumed(isPaused: boolean) {
    if (isPaused) {
      stopTimerSounds();
    }
  }

  function charadesReadyButtonOnClick() {
    if (!gameData || !question) return;
    setCountdownTimerSeconds(
      gameData.difficultiesMap.get(
        difficultyStringToDifficulty(question.difficulty!)
      )!
    );
    setIsCharadesReadyToPlay(true);
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    init,
    question,
    answer,
    gameData,
    countdownTimerSeconds,
    setCountdownTimerSeconds,
    setTimerPausedOrResumed,
    findIfAnyLifelinesIsSelectedBeforeTheQuestion,
    onLifelineClicked,
    showAnswer,
    setShowAnswer,
    hasFirstTeamAnswered,
    bothTeamAnswered,
    onAnswerButtonClicked,
    showWhoAnswered,
    setShowWhoAnswered,
    videoFinished,
    setVideoFinished,
    giveRating,
    isRatingGiven,
    giveFeedbackDialogOpen,
    setGiveFeedbackDialogOpen,
    charadesQRCodeImage,
    isCharadesReadyToPlay,
    isCharadesCategory,
    charadesReadyButtonOnClick,
  };
}
